import { formatISO } from "date-fns";
import { uuidv7 } from "uuidv7";
import { DEFAULT_INVOICE_FONT_ID } from "@common/constants/invoiceDesign";
import {
    NATIVE_INVOICE_DEFAULT_PAYMENT_METHODS,
    NativeInvoicePaymentMethod,
} from "@common/constants/nativeInvoicePaymentMethod";
import { Address } from "@common/types/embeddable/address";
import { Entity } from "@common/types/entity";
import {
    NativeInvoiceCustomTermValue,
    NativeInvoiceTermType,
} from "@common/helpers/invoice-terms.helper";
import { NativeInvoiceAdjustmentSettings } from "@common/schemas/domains/invoice/native-invoice-adjustment-settings.schema";
import {
    NativeInvoiceEmailSnapshot,
    NativeInvoiceRenderSnapshot,
} from "@common/types/nativeInvoice";
import { nativeInvoiceAdjustmentSettingsFromResponse } from "./nativeInvoiceAdjustmentCoding";
import { createDefaultNativeInvoicePdfFieldSettings } from "@common/helpers/applyNativeInvoicePdfFieldSettings";
import { hydrateNativeInvoiceFormFromSnapshot } from "./hydrateNativeInvoiceFormFromSnapshot";
import { fromStateFromEntity } from "./copyNativeInvoicePartyDetails";
import {
    defaultLine,
    parseRecipientList,
    prefillLineToRow,
    withCustomerEmailInTo,
} from "./nativeInvoiceFormHelpers";
import {
    DEFAULT_NATIVE_INVOICE_ADJUSTMENT_MODE,
    DEFAULT_NATIVE_INVOICE_FOOTER,
    DEFAULT_NATIVE_INVOICE_FOOTER_ALIGN,
    DEFAULT_NATIVE_INVOICE_PAGE_SIZE,
    NativeInvoiceCreatePrefill,
    NativeInvoiceCustomer,
    NativeInvoiceFormRawState,
    NativeInvoiceLineCodingByLineId,
} from "./nativeInvoiceFormTypes";

export interface CreateNativeInvoiceFormStateResult {
    raw: NativeInvoiceFormRawState;
    lineCodingBaselineByLineId: NativeInvoiceLineCodingByLineId;
}

interface CreateFormArgs {
    mode: "create";
    entity?: Entity;
    entityAddress?: Address | null;
    currentUserName?: string | null;
    currentUserEmail?: string | null;
    // Opens the drawer directly into a pre-filled create draft (ad-hoc lines +
    // optional customer/date/note fields) instead of the blank single-line
    // default. See NativeInvoiceCreatePrefill.
    prefill?: NativeInvoiceCreatePrefill;
}

interface EditFormArgs {
    mode: "edit";
    snapshot: NativeInvoiceRenderSnapshot;
    entity?: Entity;
    customer?: NativeInvoiceCustomer | null;
    logoPreviewUrl?: string | null;
    lineCodingByLineId?: NativeInvoiceLineCodingByLineId;
    currentUserName?: string | null;
    currentUserEmail?: string | null;
    sendingEmailId?: string | null;
    collectPayment?: boolean;
    stripeFinancialAccountId?: number | null;
    acceptedPaymentMethods?: NativeInvoicePaymentMethod[];
    emailSnapshot?: NativeInvoiceEmailSnapshot | null;
    termType?: NativeInvoiceTermType | null;
    customTerm?: NativeInvoiceCustomTermValue | null;
    isEmailSubjectAutomatic?: boolean;
    isEmailMessageAutomatic?: boolean;
    adjustmentSettings?: NativeInvoiceAdjustmentSettings | null;
}

type CreateNativeInvoiceFormStateArgs = CreateFormArgs | EditFormArgs;

// Build a fully-resolved initial form once data is ready. The drawer gates mount
// on readiness so this runs with sync inputs — no prop-chasing effects needed.
export function createNativeInvoiceFormState(
    args: CreateNativeInvoiceFormStateArgs,
): CreateNativeInvoiceFormStateResult {
    if (args.mode === "edit") {
        const hydrated = hydrateNativeInvoiceFormFromSnapshot({
            snapshot: args.snapshot,
            counterparty: args.customer,
            logoPreviewUrl: args.logoPreviewUrl,
            lineCodingByLineId: args.lineCodingByLineId,
            generateId: uuidv7,
        });
        const {
            invoiceNumber: hydratedInvoiceNumber,
            from,
            ...hydratedRest
        } = hydrated;
        return {
            raw: {
                ...hydratedRest,
                from: args.entity
                    ? { ...from, businessName: args.entity.name ?? "" }
                    : from,
                termType: args.termType ?? null,
                customTerm: args.customTerm ?? null,
                invoiceNumberOverride: hydratedInvoiceNumber || null,
                replyToNameOverride: null,
                replyToEmailOverride: null,
                fromEmailOverride: hydrated.from.email || null,
                emailSubjectOverride:
                    args.isEmailSubjectAutomatic === true
                        ? null
                        : (args.emailSnapshot?.subject ?? null),
                emailMessageOverride:
                    args.isEmailMessageAutomatic === true
                        ? null
                        : (args.emailSnapshot?.message ?? null),
                emailTo: args.emailSnapshot
                    ? parseRecipientList(args.emailSnapshot.to).join(", ")
                    : withCustomerEmailInTo({
                          emailTo: "",
                          nextCustomerEmail: hydrated.customer?.email,
                      }),
                emailCc: (args.emailSnapshot?.cc ?? []).join(", "),
                emailBcc: (args.emailSnapshot?.bcc ?? []).join(", "),
                emailAttachPdf: args.emailSnapshot?.attachPdf ?? true,
                sendEmailEnabled: true,
                scheduleEnabled: false,
                scheduledSendAt: "",
                sendingEmailId: args.sendingEmailId ?? null,
                collectPayment: args.collectPayment ?? false,
                stripeFinancialAccountId: args.stripeFinancialAccountId ?? null,
                acceptedPaymentMethods:
                    args.acceptedPaymentMethods ??
                    NATIVE_INVOICE_DEFAULT_PAYMENT_METHODS,
                adjustmentSettings: nativeInvoiceAdjustmentSettingsFromResponse(
                    args.adjustmentSettings,
                ),
            },
            lineCodingBaselineByLineId: args.lineCodingByLineId ?? {},
        };
    }

    const from = fromStateFromEntity({
        entity: args.entity,
        fallbackEmail: args.currentUserEmail,
        address: args.entityAddress,
    });
    const prefill = args.prefill;
    const prefillLines = prefill?.lines.map(prefillLineToRow);

    return {
        raw: {
            from,
            customer: prefill?.customer ?? null,
            toTaxId: prefill?.toTaxId ?? "",
            toFullName: prefill?.toFullName ?? "",
            toCompany: prefill?.toCompany ?? "",
            invoiceNumberOverride: null,
            replyToNameOverride: null,
            replyToEmailOverride: null,
            fromEmailOverride: null,
            invoiceDate:
                prefill?.invoiceDate ??
                formatISO(new Date(), { representation: "date" }),
            dueDate: prefill?.dueDate ?? "",
            poNumber: prefill?.poNumber ?? "",
            note: prefill?.note ?? "",
            terms: prefill?.terms ?? "",
            termType: null,
            customTerm: null,
            footer: DEFAULT_NATIVE_INVOICE_FOOTER,
            footerAlign: DEFAULT_NATIVE_INVOICE_FOOTER_ALIGN,
            showPagination: true,
            tax: 0,
            discount: 0,
            deposit: 0,
            taxMode: DEFAULT_NATIVE_INVOICE_ADJUSTMENT_MODE,
            discountMode: DEFAULT_NATIVE_INVOICE_ADJUSTMENT_MODE,
            depositMode: "amount",
            isTaxPerLineItem: false,
            isDiscountPerLineItem: false,
            isDepositPerLineItem: false,
            isTaxDisplayedPerLineItem: false,
            isDiscountDisplayedPerLineItem: false,
            isDepositDisplayedPerLineItem: false,
            pageSize: DEFAULT_NATIVE_INVOICE_PAGE_SIZE,
            logoPath: null,
            logoPreviewUrl: null,
            backgroundColor: null,
            accentColor: null,
            textColor: null,
            fontFamily: DEFAULT_INVOICE_FONT_ID,
            labelOverrides: null,
            fieldSettings: createDefaultNativeInvoicePdfFieldSettings(),
            lines:
                prefillLines && prefillLines.length > 0
                    ? prefillLines
                    : [defaultLine()],
            emailSubjectOverride: null,
            emailMessageOverride: null,
            emailTo: withCustomerEmailInTo({
                emailTo: "",
                nextCustomerEmail: prefill?.customer?.email,
            }),
            emailCc: "",
            emailBcc: "",
            emailAttachPdf: true,
            sendEmailEnabled: true,
            scheduleEnabled: false,
            scheduledSendAt: "",
            sendingEmailId: null,
            collectPayment: false,
            stripeFinancialAccountId: null,
            acceptedPaymentMethods: NATIVE_INVOICE_DEFAULT_PAYMENT_METHODS,
            adjustmentSettings:
                nativeInvoiceAdjustmentSettingsFromResponse(null),
        },
        lineCodingBaselineByLineId: {},
    };
}
